import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { MILAN_WORKSPACE_ENABLED } from '../config/productFlags';
import './Resources.css';

function Resources() {
  const [activeTool, setActiveTool] = useState(null);

  const tools = [
    {
      id: 'comparison',
      name: 'Influencer Comparison',
      description: 'Compare multiple influencers side by side based on their metrics',
      icon: '⚖️',
      color: '#3b82f6'
    },
    {
      id: 'fake-follower',
      name: 'Fake Follower Estimator',
      description: 'Estimate potential fake followers based on account metrics',
      icon: '🔍',
      color: '#f59e0b'
    },
    {
      id: 'engagement',
      name: 'Engagement Rate Calculator',
      description: 'Calculate engagement rate based on followers, likes, and comments',
      icon: '📊',
      color: '#f97316'
    },
    {
      id: 'pricing',
      name: 'Pricing Estimator',
      description: 'Get estimated pricing for influencer collaborations based on metrics',
      icon: '💰',
      color: '#8b5cf6'
    }
  ];

  return (
    <div className="resources-page">
      <Navbar />
      
      <section className="resources-hero">
        <h1>Free Marketing Tools</h1>
        <p>Helpful calculators and tools for influencer marketing — free to use, no signup required</p>
      </section>

      <section className="tools-section">
        <div className="tools-grid">
          {tools.map(tool => (
            <div 
              key={tool.id} 
              className="tool-card"
              onClick={() => setActiveTool(tool.id)}
              style={{ '--tool-color': tool.color }}
            >
              <span className="tool-icon">{tool.icon}</span>
              <h3>{tool.name}</h3>
              <p>{tool.description}</p>
              <button className="tool-btn">Use Tool →</button>
            </div>
          ))}
        </div>
      </section>

      {/* Tool Modals */}
      {activeTool && (
        <div className="tool-modal-overlay" onClick={() => setActiveTool(null)}>
          <div className="tool-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setActiveTool(null)}>×</button>
            
            {activeTool === 'comparison' && <ComparisonTool />}
            {activeTool === 'fake-follower' && <FakeFollowerTool />}
            {activeTool === 'engagement' && <EngagementTool />}
            {activeTool === 'pricing' && <PricingTool />}
          </div>
        </div>
      )}

      <section className="resources-cta">
        <h2>Need more advanced features?</h2>
        <p>
          {MILAN_WORKSPACE_ENABLED
            ? 'Sign up for free to access AI-powered matching, campaign management, and more'
            : 'Sign up for free to open Performance Hub and track Instagram metrics for your roster'}
        </p>
        <Link to="/signup" className="cta-btn">Get Started Free</Link>
      </section>

      <Footer />
    </div>
  );
}

// Comparison Tool Component
function ComparisonTool() {
  const [influencers, setInfluencers] = useState([
    { name: '', followers: '', engagement: '', avgLikes: '', avgComments: '' },
    { name: '', followers: '', engagement: '', avgLikes: '', avgComments: '' }
  ]);

  const updateInfluencer = (index, field, value) => {
    const updated = [...influencers];
    updated[index][field] = value;
    setInfluencers(updated);
  };

  const addInfluencer = () => {
    if (influencers.length < 4) {
      setInfluencers([...influencers, { name: '', followers: '', engagement: '', avgLikes: '', avgComments: '' }]);
    }
  };

  const getScoreDetails = (inf) => {
    const followers = parseInt(inf.followers) || 0;
    const engagement = parseFloat(inf.engagement) || 0;
    const avgLikes = parseInt(inf.avgLikes) || 0;
    const avgComments = parseInt(inf.avgComments) || 0;
    
    if (!followers) return { score: 0, grade: '-', breakdown: null };
    
    // 1. ENGAGEMENT SCORE (0-50 points) - Most important factor
    // Industry benchmarks: <1% poor, 1-2% below avg, 2-4% average, 4-6% good, >6% excellent
    let engagementScore = 0;
    if (engagement >= 8) engagementScore = 50;
    else if (engagement >= 6) engagementScore = 45;
    else if (engagement >= 4) engagementScore = 38;
    else if (engagement >= 3) engagementScore = 30;
    else if (engagement >= 2) engagementScore = 22;
    else if (engagement >= 1) engagementScore = 15;
    else if (engagement >= 0.5) engagementScore = 8;
    else engagementScore = 3;
    
    // 2. FOLLOWER TIER BONUS (0-20 points)
    // Smaller accounts typically have better engagement and authenticity
    let tierBonus = 0;
    let tierName = '';
    if (followers < 10000) { tierBonus = 20; tierName = 'Nano'; }
    else if (followers < 50000) { tierBonus = 18; tierName = 'Micro'; }
    else if (followers < 100000) { tierBonus = 16; tierName = 'Small'; }
    else if (followers < 500000) { tierBonus = 14; tierName = 'Mid-tier'; }
    else if (followers < 1000000) { tierBonus = 12; tierName = 'Macro'; }
    else { tierBonus = 10; tierName = 'Mega'; }
    
    // 3. ENGAGEMENT QUALITY (0-20 points)
    // Verifies stated engagement with actual metrics if provided
    let qualityScore = 10; // Default neutral score
    if (avgLikes > 0) {
      const calculatedEngagement = ((avgLikes + avgComments) / followers) * 100;
      const difference = Math.abs(engagement - calculatedEngagement);
      
      if (difference < 0.5) qualityScore = 20;
      else if (difference < 1) qualityScore = 17;
      else if (difference < 2) qualityScore = 14;
      else if (difference < 3) qualityScore = 10;
      else if (difference < 5) qualityScore = 6;
      else qualityScore = 3;
    }
    
    // 4. COMMENT RATIO (0-10 points)
    // Higher comment ratios indicate genuine engagement
    let commentScore = 5; // Default
    if (avgLikes > 0 && avgComments > 0) {
      const commentRatio = (avgComments / avgLikes) * 100;
      if (commentRatio >= 10) commentScore = 10;
      else if (commentRatio >= 5) commentScore = 8;
      else if (commentRatio >= 3) commentScore = 6;
      else if (commentRatio >= 1) commentScore = 4;
      else commentScore = 2;
    }
    
    const totalScore = engagementScore + tierBonus + qualityScore + commentScore;
    
    // Determine grade
    let grade = 'F';
    if (totalScore >= 85) grade = 'A+';
    else if (totalScore >= 75) grade = 'A';
    else if (totalScore >= 65) grade = 'B+';
    else if (totalScore >= 55) grade = 'B';
    else if (totalScore >= 45) grade = 'C+';
    else if (totalScore >= 35) grade = 'C';
    else if (totalScore >= 25) grade = 'D';
    else grade = 'F';
    
    return {
      score: totalScore,
      grade,
      breakdown: {
        engagement: engagementScore,
        tier: tierBonus,
        tierName,
        quality: qualityScore,
        comments: commentScore
      }
    };
  };

  const getGradeColor = (grade) => {
    const colors = {
      'A+': '#10b981', 'A': '#22c55e', 
      'B+': '#84cc16', 'B': '#a3e635',
      'C+': '#facc15', 'C': '#fbbf24',
      'D': '#f97316', 'F': '#ef4444'
    };
    return colors[grade] || '#6b7280';
  };

  return (
    <div className="tool-content">
      <h2>⚖️ Influencer Comparison Tool</h2>
      <p>Compare up to 4 influencers based on engagement quality and authenticity</p>
      
      <div className="comparison-grid">
        {influencers.map((inf, idx) => {
          const details = getScoreDetails(inf);
          return (
            <div key={idx} className="comparison-card">
              <h4>Influencer {idx + 1}</h4>
              <input
                type="text"
                placeholder="Name / Handle"
                value={inf.name}
                onChange={e => updateInfluencer(idx, 'name', e.target.value)}
              />
              <input
                type="number"
                placeholder="Followers"
                value={inf.followers}
                onChange={e => updateInfluencer(idx, 'followers', e.target.value)}
              />
              <input
                type="number"
                placeholder="Engagement Rate %"
                value={inf.engagement}
                onChange={e => updateInfluencer(idx, 'engagement', e.target.value)}
                step="0.1"
              />
              <input
                type="number"
                placeholder="Avg Likes per Post"
                value={inf.avgLikes}
                onChange={e => updateInfluencer(idx, 'avgLikes', e.target.value)}
              />
              <input
                type="number"
                placeholder="Avg Comments per Post"
                value={inf.avgComments}
                onChange={e => updateInfluencer(idx, 'avgComments', e.target.value)}
              />
              <div className="score-display" style={{ background: details.score > 0 ? getGradeColor(details.grade) + '15' : undefined }}>
                <div className="score-main">
                  <span className="score-number">{details.score}</span>
                  <span className="score-max">/100</span>
                  <span className="score-grade" style={{ color: getGradeColor(details.grade) }}>{details.grade}</span>
                </div>
                {details.breakdown && (
                  <div className="score-breakdown">
                    <span title="Engagement Rate Score">📊 {details.breakdown.engagement}/50</span>
                    <span title="Follower Tier Bonus">👥 {details.breakdown.tier}/20 ({details.breakdown.tierName})</span>
                    <span title="Data Quality Score">✓ {details.breakdown.quality}/20</span>
                    <span title="Comment Ratio Score">💬 {details.breakdown.comments}/10</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="score-legend">
        <h4>Score Breakdown</h4>
        <ul>
          <li><strong>Engagement (50pts):</strong> Based on industry benchmarks - &gt;6% excellent, 3-6% good, 1-3% average</li>
          <li><strong>Tier Bonus (20pts):</strong> Smaller accounts often have more authentic engagement</li>
          <li><strong>Quality (20pts):</strong> Verifies stated engagement matches actual likes/comments</li>
          <li><strong>Comments (10pts):</strong> Higher comment ratios indicate genuine audience interaction</li>
        </ul>
      </div>
      
      {influencers.length < 4 && (
        <button className="add-btn" onClick={addInfluencer}>+ Add Another Influencer</button>
      )}
    </div>
  );
}

// Fake Follower Calculator - Industry Standard Algorithm
function FakeFollowerTool() {
  const [data, setData] = useState({
    followers: '',
    following: '',
    posts: '',
    avgLikes: '',
    avgComments: '',
    accountAge: '12'
  });
  const [result, setResult] = useState(null);

  const calculate = () => {
    const followers = parseInt(data.followers) || 0;
    const following = parseInt(data.following) || 0;
    const posts = parseInt(data.posts) || 0;
    const avgLikes = parseInt(data.avgLikes) || 0;
    const avgComments = parseInt(data.avgComments) || 0;
    const accountAge = parseInt(data.accountAge) || 12;

    if (!followers) return;

    const engagementRate = ((avgLikes + avgComments) / followers) * 100;
    const followRatio = followers > 0 ? following / followers : 0;
    const commentToLikeRatio = avgLikes > 0 ? (avgComments / avgLikes) * 100 : 0;
    const followersPerMonth = accountAge > 0 ? followers / accountAge : followers;
    const postsPerMonth = accountAge > 0 ? posts / accountAge : posts;

    // Determine follower tier for expected engagement benchmarks
    let expectedEngagement = { min: 1, max: 3 };
    let tierName = '';
    if (followers < 10000) { expectedEngagement = { min: 3, max: 8 }; tierName = 'Nano'; }
    else if (followers < 50000) { expectedEngagement = { min: 2, max: 5 }; tierName = 'Micro'; }
    else if (followers < 100000) { expectedEngagement = { min: 1.5, max: 4 }; tierName = 'Small'; }
    else if (followers < 500000) { expectedEngagement = { min: 1, max: 3 }; tierName = 'Mid-tier'; }
    else if (followers < 1000000) { expectedEngagement = { min: 0.8, max: 2.5 }; tierName = 'Macro'; }
    else { expectedEngagement = { min: 0.5, max: 2 }; tierName = 'Mega'; }

    let suspicionScore = 0;
    let reasons = [];
    let positives = [];

    // 1. Following-to-Follower Ratio Check (0-15 points)
    if (followRatio > 2) { 
      suspicionScore += 15; 
      reasons.push({ text: 'Very high following ratio (>2:1) - common in follow/unfollow schemes', severity: 'high' });
    } else if (followRatio > 1) { 
      suspicionScore += 8; 
      reasons.push({ text: 'High following ratio (>1:1) - may indicate artificial growth', severity: 'medium' });
    } else if (followRatio < 0.1 && followers > 10000) {
      positives.push('Low following ratio indicates organic celebrity-like growth');
    }

    // 2. Engagement Rate vs Tier Expected (0-25 points)
    if (engagementRate < expectedEngagement.min * 0.3) {
      suspicionScore += 25;
      reasons.push({ text: `Extremely low engagement (${engagementRate.toFixed(2)}%) for ${tierName} tier (expected ${expectedEngagement.min}-${expectedEngagement.max}%)`, severity: 'high' });
    } else if (engagementRate < expectedEngagement.min * 0.6) {
      suspicionScore += 15;
      reasons.push({ text: `Low engagement (${engagementRate.toFixed(2)}%) for ${tierName} tier`, severity: 'medium' });
    } else if (engagementRate > 15 && followers > 50000) {
      suspicionScore += 20;
      reasons.push({ text: `Unusually high engagement (${engagementRate.toFixed(2)}%) for this follower count - possible bot activity`, severity: 'high' });
    } else if (engagementRate >= expectedEngagement.min && engagementRate <= expectedEngagement.max) {
      positives.push(`Healthy engagement rate for ${tierName} tier`);
    }

    // 3. Comment-to-Like Ratio (0-15 points)
    // Industry standard: 1-5% comments per like is normal
    if (avgLikes > 0 && avgComments > 0) {
      if (commentToLikeRatio < 0.5) {
        suspicionScore += 12;
        reasons.push({ text: 'Very few comments relative to likes - possible like bots', severity: 'medium' });
      } else if (commentToLikeRatio > 20) {
        suspicionScore += 15;
        reasons.push({ text: 'Abnormally high comments vs likes - possible comment bots', severity: 'high' });
      } else if (commentToLikeRatio >= 1 && commentToLikeRatio <= 5) {
        positives.push('Healthy comment-to-like ratio');
      }
    }

    // 4. Posts vs Followers Ratio (0-15 points)
    if (posts < 10 && followers > 10000) {
      suspicionScore += 15;
      reasons.push({ text: 'Very few posts but many followers - suspicious growth pattern', severity: 'high' });
    } else if (posts < 30 && followers > 50000) {
      suspicionScore += 10;
      reasons.push({ text: 'Low post count relative to followers', severity: 'medium' });
    }

    // 5. Growth Rate Analysis (0-15 points)
    if (followersPerMonth > 50000 && followers < 500000) {
      suspicionScore += 15;
      reasons.push({ text: `Rapid growth (${Math.round(followersPerMonth).toLocaleString()}/month) - may indicate purchased followers`, severity: 'high' });
    } else if (followersPerMonth > 20000 && followers < 200000) {
      suspicionScore += 8;
      reasons.push({ text: 'Above-average growth rate - verify content quality', severity: 'medium' });
    }

    // 6. Posting Consistency (0-10 points)
    if (postsPerMonth < 1 && followers > 5000) {
      suspicionScore += 10;
      reasons.push({ text: 'Very low posting frequency for follower count', severity: 'medium' });
    } else if (postsPerMonth >= 4 && postsPerMonth <= 30) {
      positives.push('Consistent posting schedule');
    }

    // 7. Engagement Quality Check (0-5 points)
    if (avgLikes > 0 && avgComments === 0) {
      suspicionScore += 5;
      reasons.push({ text: 'Zero comments on posts - unusual for genuine audiences', severity: 'low' });
    }

    // Calculate final score (cap at 100)
    const fakePercentage = Math.min(Math.round(suspicionScore), 100);
    const realFollowers = Math.round(followers * (1 - fakePercentage / 100));

    // Determine risk level
    let riskLevel = 'Low Risk';
    let riskColor = '#10b981';
    if (fakePercentage >= 60) { riskLevel = 'High Risk'; riskColor = '#ef4444'; }
    else if (fakePercentage >= 40) { riskLevel = 'Medium Risk'; riskColor = '#f59e0b'; }
    else if (fakePercentage >= 20) { riskLevel = 'Low-Medium Risk'; riskColor = '#eab308'; }

    setResult({
      fakePercentage,
      realFollowers,
      engagementRate: engagementRate.toFixed(2),
      commentToLikeRatio: commentToLikeRatio.toFixed(1),
      reasons,
      positives,
      riskLevel,
      riskColor,
      tierName,
      expectedEngagement
    });
  };

  return (
    <div className="tool-content">
      <h2>🔍 Fake Follower Analyzer</h2>
      <p>Advanced analysis using industry-standard authenticity metrics</p>
      
      <div className="tool-form">
        <div className="form-row">
          <input type="number" placeholder="Total Followers" value={data.followers}
            onChange={e => setData({...data, followers: e.target.value})} />
          <input type="number" placeholder="Total Following" value={data.following}
            onChange={e => setData({...data, following: e.target.value})} />
        </div>
        <div className="form-row">
          <input type="number" placeholder="Total Posts" value={data.posts}
            onChange={e => setData({...data, posts: e.target.value})} />
          <input type="number" placeholder="Account Age (months)" value={data.accountAge}
            onChange={e => setData({...data, accountAge: e.target.value})} />
        </div>
        <div className="form-row">
          <input type="number" placeholder="Avg Likes per Post" value={data.avgLikes}
            onChange={e => setData({...data, avgLikes: e.target.value})} />
          <input type="number" placeholder="Avg Comments per Post" value={data.avgComments}
            onChange={e => setData({...data, avgComments: e.target.value})} />
        </div>
        
        <button className="calculate-btn" onClick={calculate}>Analyze Account</button>
      </div>

      {result && (
        <div className="result-box fake-follower-result">
          <div className="risk-header" style={{ background: result.riskColor + '15', borderColor: result.riskColor }}>
            <div className="risk-circle" style={{ background: result.riskColor }}>
              {result.fakePercentage}%
            </div>
            <div className="risk-info">
              <span className="risk-label" style={{ color: result.riskColor }}>{result.riskLevel}</span>
              <span className="risk-tier">{result.tierName} Influencer</span>
            </div>
          </div>
          
          <div className="result-meter">
            <div className="meter-fill" style={{ width: `${result.fakePercentage}%`, background: result.riskColor }}></div>
          </div>
          
          <div className="result-stats">
            <div>
              <span>Estimated Real</span>
              <strong>~{result.realFollowers.toLocaleString()}</strong>
            </div>
            <div>
              <span>Engagement Rate</span>
              <strong>{result.engagementRate}%</strong>
              <small>Expected: {result.expectedEngagement.min}-{result.expectedEngagement.max}%</small>
            </div>
            <div>
              <span>Comment/Like Ratio</span>
              <strong>{result.commentToLikeRatio}%</strong>
              <small>Healthy: 1-5%</small>
            </div>
          </div>

          {result.positives.length > 0 && (
            <div className="result-positives">
              <h4>✓ Positive Signals</h4>
              <ul>{result.positives.map((p, i) => <li key={i}>{p}</li>)}</ul>
            </div>
          )}
          
          {result.reasons.length > 0 && (
            <div className="result-reasons">
              <h4>⚠️ Warning Signs</h4>
              <ul>{result.reasons.map((r, i) => (
                <li key={i} className={`severity-${r.severity}`}>{r.text}</li>
              ))}</ul>
            </div>
          )}
          
          <div className="analysis-note">
            <strong>Note:</strong> This is an estimation based on publicly available metrics. 
            For accurate verification, consider using professional audit tools that analyze 
            follower demographics, engagement patterns over time, and audience authenticity.
          </div>
        </div>
      )}
    </div>
  );
}

// Engagement Rate Calculator - Industry Standard with Tier Benchmarks
function EngagementTool() {
  const [data, setData] = useState({ 
    followers: '', 
    likes: '', 
    comments: '', 
    saves: '',
    shares: '',
    platform: 'instagram'
  });
  const [result, setResult] = useState(null);

  // Industry benchmark data by platform and tier (2024-2025)
  const benchmarks = {
    instagram: {
      nano: { min: 4, avg: 6, max: 10, label: 'Nano (1K-10K)' },
      micro: { min: 2.5, avg: 4, max: 6, label: 'Micro (10K-50K)' },
      small: { min: 1.8, avg: 3, max: 4.5, label: 'Small (50K-100K)' },
      mid: { min: 1.2, avg: 2, max: 3.5, label: 'Mid-tier (100K-500K)' },
      macro: { min: 0.8, avg: 1.5, max: 2.5, label: 'Macro (500K-1M)' },
      mega: { min: 0.5, avg: 1, max: 2, label: 'Mega (1M+)' }
    },
    youtube: {
      nano: { min: 3, avg: 5, max: 8, label: 'Nano (1K-10K)' },
      micro: { min: 2, avg: 3.5, max: 5, label: 'Micro (10K-50K)' },
      small: { min: 1.5, avg: 2.5, max: 4, label: 'Small (50K-100K)' },
      mid: { min: 1, avg: 1.8, max: 3, label: 'Mid-tier (100K-500K)' },
      macro: { min: 0.5, avg: 1.2, max: 2, label: 'Macro (500K-1M)' },
      mega: { min: 0.3, avg: 0.8, max: 1.5, label: 'Mega (1M+)' }
    },
    tiktok: {
      nano: { min: 6, avg: 10, max: 15, label: 'Nano (1K-10K)' },
      micro: { min: 4, avg: 7, max: 10, label: 'Micro (10K-50K)' },
      small: { min: 3, avg: 5, max: 8, label: 'Small (50K-100K)' },
      mid: { min: 2, avg: 4, max: 6, label: 'Mid-tier (100K-500K)' },
      macro: { min: 1.5, avg: 3, max: 5, label: 'Macro (500K-1M)' },
      mega: { min: 1, avg: 2, max: 4, label: 'Mega (1M+)' }
    }
  };

  const calculate = () => {
    const followers = parseInt(data.followers) || 0;
    const likes = parseInt(data.likes) || 0;
    const comments = parseInt(data.comments) || 0;
    const saves = parseInt(data.saves) || 0;
    const shares = parseInt(data.shares) || 0;

    if (!followers) return;

    // Standard engagement calculation (likes + comments)
    const basicEngagement = likes + comments;
    const basicRate = (basicEngagement / followers) * 100;

    // Full engagement (including saves and shares for Instagram)
    const fullEngagement = likes + comments + saves + shares;
    const fullRate = (fullEngagement / followers) * 100;

    // Determine tier
    let tier = 'mega';
    if (followers < 10000) tier = 'nano';
    else if (followers < 50000) tier = 'micro';
    else if (followers < 100000) tier = 'small';
    else if (followers < 500000) tier = 'mid';
    else if (followers < 1000000) tier = 'macro';

    const benchmark = benchmarks[data.platform][tier];

    // Calculate performance vs benchmark
    let rating = 'Below Average';
    let color = '#ef4444';
    let percentile = 0;

    if (basicRate >= benchmark.max) {
      rating = 'Excellent';
      color = '#10b981';
      percentile = 95;
    } else if (basicRate >= benchmark.avg) {
      rating = 'Above Average';
      color = '#22c55e';
      percentile = 75;
    } else if (basicRate >= benchmark.min) {
      rating = 'Average';
      color = '#f59e0b';
      percentile = 50;
    } else if (basicRate >= benchmark.min * 0.5) {
      rating = 'Below Average';
      color = '#f97316';
      percentile = 25;
    } else {
      rating = 'Poor';
      color = '#ef4444';
      percentile = 10;
    }

    // Calculate estimated reach
    const estimatedReach = Math.round(followers * (basicRate / 100) * 10);

    setResult({ 
      basicRate: basicRate.toFixed(2), 
      fullRate: fullRate.toFixed(2),
      rating, 
      color, 
      basicEngagement,
      fullEngagement,
      tier,
      benchmark,
      percentile,
      estimatedReach,
      saves,
      shares
    });
  };

  return (
    <div className="tool-content">
      <h2>📊 Engagement Rate Calculator</h2>
      <p>Calculate engagement rate with tier-specific industry benchmarks</p>
      
      <div className="tool-form">
        <div className="form-row">
          <input type="number" placeholder="Total Followers" value={data.followers}
            onChange={e => setData({...data, followers: e.target.value})} />
          <select value={data.platform} onChange={e => setData({...data, platform: e.target.value})}>
            <option value="instagram">Instagram</option>
            <option value="youtube">YouTube</option>
            <option value="tiktok">TikTok</option>
          </select>
        </div>
        <div className="form-row">
          <input type="number" placeholder="Total Likes" value={data.likes}
            onChange={e => setData({...data, likes: e.target.value})} />
          <input type="number" placeholder="Total Comments" value={data.comments}
            onChange={e => setData({...data, comments: e.target.value})} />
        </div>
        <div className="form-row">
          <input type="number" placeholder="Saves (optional)" value={data.saves}
            onChange={e => setData({...data, saves: e.target.value})} />
          <input type="number" placeholder="Shares (optional)" value={data.shares}
            onChange={e => setData({...data, shares: e.target.value})} />
        </div>
        
        <button className="calculate-btn" onClick={calculate}>Calculate Rate</button>
      </div>

      {result && (
        <div className="result-box engagement-result">
          <div className="engagement-header">
            <div className="big-number" style={{ color: result.color }}>
              {result.basicRate}%
            </div>
            <div className="rating-badge" style={{ background: result.color }}>
              {result.rating}
            </div>
          </div>
          
          <div className="tier-comparison">
            <div className="tier-label">{result.benchmark.label}</div>
            <div className="tier-bar">
              <div className="tier-range" style={{ 
                left: `${(result.benchmark.min / 15) * 100}%`,
                width: `${((result.benchmark.max - result.benchmark.min) / 15) * 100}%`
              }}>
                <span className="range-label">Expected: {result.benchmark.min}-{result.benchmark.max}%</span>
              </div>
              <div className="tier-marker" style={{ 
                left: `${Math.min((parseFloat(result.basicRate) / 15) * 100, 100)}%`,
                background: result.color
              }}>
                <span>{result.basicRate}%</span>
              </div>
            </div>
            <div className="tier-scale">
              <span>0%</span>
              <span>5%</span>
              <span>10%</span>
              <span>15%+</span>
            </div>
          </div>

          <div className="engagement-stats">
            <div className="stat-card">
              <span className="stat-icon">👍</span>
              <div className="stat-info">
                <strong>{result.basicEngagement.toLocaleString()}</strong>
                <span>Likes + Comments</span>
              </div>
            </div>
            {(result.saves > 0 || result.shares > 0) && (
              <div className="stat-card">
                <span className="stat-icon">📊</span>
                <div className="stat-info">
                  <strong>{result.fullRate}%</strong>
                  <span>Full Engagement Rate</span>
                </div>
              </div>
            )}
            <div className="stat-card">
              <span className="stat-icon">👁️</span>
              <div className="stat-info">
                <strong>~{result.estimatedReach.toLocaleString()}</strong>
                <span>Est. Reach</span>
              </div>
            </div>
            <div className="stat-card">
              <span className="stat-icon">📈</span>
              <div className="stat-info">
                <strong>Top {100 - result.percentile}%</strong>
                <span>In {result.benchmark.label.split(' ')[0]} Tier</span>
              </div>
            </div>
          </div>

          <div className="benchmark-info">
            <h4>Industry Benchmarks ({data.platform.charAt(0).toUpperCase() + data.platform.slice(1)})</h4>
            <div className="benchmark-grid">
              <div className="benchmark-item">
                <span className="benchmark-tier">Nano (1K-10K)</span>
                <span className="benchmark-rate">{benchmarks[data.platform].nano.avg}% avg</span>
              </div>
              <div className="benchmark-item">
                <span className="benchmark-tier">Micro (10K-50K)</span>
                <span className="benchmark-rate">{benchmarks[data.platform].micro.avg}% avg</span>
              </div>
              <div className="benchmark-item">
                <span className="benchmark-tier">Mid (100K-500K)</span>
                <span className="benchmark-rate">{benchmarks[data.platform].mid.avg}% avg</span>
              </div>
              <div className="benchmark-item">
                <span className="benchmark-tier">Mega (1M+)</span>
                <span className="benchmark-rate">{benchmarks[data.platform].mega.avg}% avg</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Pricing Calculator - Indian Market Rates (2024-2025)
function PricingTool() {
  const [data, setData] = useState({ 
    followers: '', 
    engagement: '', 
    platform: 'instagram',
    contentType: 'reel',
    niche: 'lifestyle',
    exclusivity: 'none'
  });
  const [result, setResult] = useState(null);

  // Indian market base rates by tier (2024-2025 rates in INR)
  const tierPricing = {
    instagram: {
      nano: { min: 1000, max: 5000 },      // 1K-10K
      micro: { min: 5000, max: 25000 },     // 10K-50K
      small: { min: 15000, max: 50000 },    // 50K-100K
      mid: { min: 30000, max: 150000 },     // 100K-500K
      macro: { min: 100000, max: 500000 },  // 500K-1M
      mega: { min: 300000, max: 2000000 }   // 1M+
    },
    youtube: {
      nano: { min: 2000, max: 10000 },
      micro: { min: 10000, max: 50000 },
      small: { min: 30000, max: 100000 },
      mid: { min: 75000, max: 300000 },
      macro: { min: 200000, max: 800000 },
      mega: { min: 500000, max: 5000000 }
    }
  };

  // Content type multipliers (relative to static post)
  const contentMultipliers = {
    instagram: {
      story: 0.25,
      post: 1,
      carousel: 1.3,
      reel: 1.8,
      'story-series': 0.6,
      'igtv': 2
    },
    youtube: {
      'community-post': 0.2,
      short: 0.5,
      'dedicated-video': 3,
      'integrated': 1,
      'review': 2
    }
  };

  // Niche multipliers (some niches command premium rates)
  const nicheMultipliers = {
    lifestyle: 1,
    fashion: 1.1,
    beauty: 1.15,
    fitness: 1,
    travel: 1.05,
    food: 0.95,
    tech: 1.3,
    finance: 1.5,
    gaming: 1.1,
    parenting: 1.05,
    education: 1.2,
    luxury: 1.8,
    automotive: 1.4,
    entertainment: 0.9
  };

  // Exclusivity multipliers
  const exclusivityMultipliers = {
    none: 1,
    'category-30': 1.3,  // 30 days category exclusivity
    'category-90': 1.5,  // 90 days category exclusivity
    'platform-30': 1.5,  // 30 days platform exclusivity
    'platform-90': 2     // 90 days platform exclusivity
  };

  const calculate = () => {
    const followers = parseInt(data.followers) || 0;
    const engagement = parseFloat(data.engagement) || 2;

    if (!followers) return;

    // Determine tier
    let tier = 'mega';
    let tierLabel = 'Mega (1M+)';
    if (followers < 10000) { tier = 'nano'; tierLabel = 'Nano (1K-10K)'; }
    else if (followers < 50000) { tier = 'micro'; tierLabel = 'Micro (10K-50K)'; }
    else if (followers < 100000) { tier = 'small'; tierLabel = 'Small (50K-100K)'; }
    else if (followers < 500000) { tier = 'mid'; tierLabel = 'Mid-tier (100K-500K)'; }
    else if (followers < 1000000) { tier = 'macro'; tierLabel = 'Macro (500K-1M)'; }

    // Get base tier pricing
    const platformKey = data.platform === 'youtube' ? 'youtube' : 'instagram';
    const basePricing = tierPricing[platformKey][tier];

    // Calculate position within tier (linear interpolation based on followers)
    const tierRanges = {
      nano: [1000, 10000],
      micro: [10000, 50000],
      small: [50000, 100000],
      mid: [100000, 500000],
      macro: [500000, 1000000],
      mega: [1000000, 10000000]
    };
    const [tierMin, tierMax] = tierRanges[tier];
    const tierPosition = Math.min((followers - tierMin) / (tierMax - tierMin), 1);
    const basePrice = basePricing.min + (basePricing.max - basePricing.min) * tierPosition;

    // Apply multipliers
    const contentMult = contentMultipliers[platformKey][data.contentType] || 1;
    const nicheMult = nicheMultipliers[data.niche] || 1;
    const exclusivityMult = exclusivityMultipliers[data.exclusivity] || 1;

    // Engagement bonus/penalty
    let engagementMult = 1;
    const expectedEngagement = tier === 'nano' ? 5 : tier === 'micro' ? 3.5 : tier === 'small' ? 2.5 : tier === 'mid' ? 2 : tier === 'macro' ? 1.5 : 1;
    if (engagement >= expectedEngagement * 1.5) engagementMult = 1.3;
    else if (engagement >= expectedEngagement) engagementMult = 1.1;
    else if (engagement < expectedEngagement * 0.5) engagementMult = 0.7;
    else if (engagement < expectedEngagement * 0.75) engagementMult = 0.85;

    const finalPrice = basePrice * contentMult * nicheMult * exclusivityMult * engagementMult;

    // Calculate CPM and CPE for reference
    const estimatedReach = followers * 0.15; // ~15% reach
    const estimatedEngagements = followers * (engagement / 100);
    const cpm = (finalPrice / estimatedReach) * 1000;
    const cpe = finalPrice / estimatedEngagements;

    setResult({
      minPrice: Math.round(finalPrice * 0.8),
      maxPrice: Math.round(finalPrice * 1.2),
      avgPrice: Math.round(finalPrice),
      tierLabel,
      breakdown: {
        base: Math.round(basePrice),
        content: contentMult,
        niche: nicheMult,
        exclusivity: exclusivityMult,
        engagement: engagementMult
      },
      metrics: {
        cpm: Math.round(cpm),
        cpe: Math.round(cpe),
        estimatedReach: Math.round(estimatedReach),
        estimatedEngagements: Math.round(estimatedEngagements)
      },
      contentType: data.contentType,
      niche: data.niche
    });
  };

  return (
    <div className="tool-content">
      <h2>💰 Influencer Pricing Calculator</h2>
      <p>Estimate fair market rates based on Indian market data (2024-2025)</p>
      
      <div className="tool-form">
        <div className="form-row">
          <input type="number" placeholder="Follower Count" value={data.followers}
            onChange={e => setData({...data, followers: e.target.value})} />
          <input type="number" placeholder="Engagement Rate %" value={data.engagement}
            onChange={e => setData({...data, engagement: e.target.value})} step="0.1" />
        </div>
        
        <div className="form-row">
          <select value={data.platform} onChange={e => setData({...data, platform: e.target.value})}>
            <option value="instagram">Instagram</option>
            <option value="youtube">YouTube</option>
          </select>
          <select value={data.contentType} onChange={e => setData({...data, contentType: e.target.value})}>
            {data.platform === 'instagram' ? (
              <>
                <option value="story">Story (24hr)</option>
                <option value="post">Static Post</option>
                <option value="carousel">Carousel</option>
                <option value="reel">Reel</option>
                <option value="story-series">Story Series (3-5)</option>
              </>
            ) : (
              <>
                <option value="community-post">Community Post</option>
                <option value="short">YouTube Short</option>
                <option value="integrated">Integrated Mention</option>
                <option value="review">Product Review</option>
                <option value="dedicated-video">Dedicated Video</option>
              </>
            )}
          </select>
        </div>
        
        <div className="form-row">
          <select value={data.niche} onChange={e => setData({...data, niche: e.target.value})}>
            <option value="lifestyle">Lifestyle</option>
            <option value="fashion">Fashion</option>
            <option value="beauty">Beauty</option>
            <option value="fitness">Fitness & Health</option>
            <option value="travel">Travel</option>
            <option value="food">Food</option>
            <option value="tech">Tech</option>
            <option value="finance">Finance/Investment</option>
            <option value="gaming">Gaming</option>
            <option value="parenting">Parenting</option>
            <option value="education">Education</option>
            <option value="luxury">Luxury</option>
            <option value="automotive">Automotive</option>
            <option value="entertainment">Entertainment</option>
          </select>
          <select value={data.exclusivity} onChange={e => setData({...data, exclusivity: e.target.value})}>
            <option value="none">No Exclusivity</option>
            <option value="category-30">Category Exclusivity (30 days)</option>
            <option value="category-90">Category Exclusivity (90 days)</option>
            <option value="platform-30">Platform Exclusivity (30 days)</option>
            <option value="platform-90">Platform Exclusivity (90 days)</option>
          </select>
        </div>
        
        <button className="calculate-btn" onClick={calculate}>Calculate Price</button>
      </div>

      {result && (
        <div className="result-box pricing-result">
          <div className="pricing-header">
            <span className="tier-badge">{result.tierLabel}</span>
            <span className="content-badge">{result.contentType.replace('-', ' ')}</span>
          </div>
          
          <h3>Estimated Price Range</h3>
          <div className="price-range">
            <span className="min-price">₹{result.minPrice.toLocaleString()}</span>
            <span className="price-arrow">→</span>
            <span className="max-price">₹{result.maxPrice.toLocaleString()}</span>
          </div>
          <div className="avg-price">
            Recommended: <strong>₹{result.avgPrice.toLocaleString()}</strong>
          </div>
          
          <div className="price-breakdown">
            <h4>Price Factors</h4>
            <div className="breakdown-grid">
              <div className="breakdown-item">
                <span>Base ({result.tierLabel.split(' ')[0]})</span>
                <span>₹{result.breakdown.base.toLocaleString()}</span>
              </div>
              <div className="breakdown-item">
                <span>Content Type</span>
                <span>{result.breakdown.content > 1 ? '+' : ''}{Math.round((result.breakdown.content - 1) * 100)}%</span>
              </div>
              <div className="breakdown-item">
                <span>Niche ({result.niche})</span>
                <span>{result.breakdown.niche > 1 ? '+' : ''}{Math.round((result.breakdown.niche - 1) * 100)}%</span>
              </div>
              {result.breakdown.exclusivity > 1 && (
                <div className="breakdown-item">
                  <span>Exclusivity</span>
                  <span>+{Math.round((result.breakdown.exclusivity - 1) * 100)}%</span>
                </div>
              )}
              <div className="breakdown-item">
                <span>Engagement Adj.</span>
                <span className={result.breakdown.engagement >= 1 ? 'positive' : 'negative'}>
                  {result.breakdown.engagement >= 1 ? '+' : ''}{Math.round((result.breakdown.engagement - 1) * 100)}%
                </span>
              </div>
            </div>
          </div>
          
          <div className="metrics-section">
            <h4>Performance Metrics</h4>
            <div className="metrics-grid">
              <div className="metric">
                <strong>₹{result.metrics.cpm}</strong>
                <span>CPM (Cost per 1K reach)</span>
              </div>
              <div className="metric">
                <strong>₹{result.metrics.cpe}</strong>
                <span>CPE (Cost per engagement)</span>
              </div>
              <div className="metric">
                <strong>{result.metrics.estimatedReach.toLocaleString()}</strong>
                <span>Est. Reach</span>
              </div>
              <div className="metric">
                <strong>{result.metrics.estimatedEngagements.toLocaleString()}</strong>
                <span>Est. Engagements</span>
              </div>
            </div>
          </div>
          
          <p className="price-note">
            *Rates based on Indian market data. Actual prices vary by audience demographics, 
            content quality, brand fit, usage rights, and negotiation.
          </p>
        </div>
      )}
    </div>
  );
}

export default Resources;
